import { Box, Stack, Typography } from '@mui/material';
import { ICertificateFullInfo } from '@repo/types';
import { UserPreview } from 'components/UI';
import { ANONYMOUS_SENDER_LABEL, isAnonymousGift } from 'utils/certificateUtils';
import { formatedDate } from 'utils/date';

/**
 * VERA-304 — the sender, through the SHARED person row, with the date on its own axis.
 *
 * This hand-rolled the `@repo/shadcn-ui` Avatar with `primaryText`/`secondaryText`, which
 * produced two defects the shared component does not have:
 *
 *  1. The HANDLE sat where the NAME goes. `getNameOrEmailSlug` collapses name and handle
 *     into one string, so a sender with a real name and a handle showed only one of them
 *     and a handle-only sender showed `@spencer2` as their title with no sub-line at all.
 *     `UserPreview` splits the two (`resolveUserIdentity`: name on top, `@handle` under
 *     it) and is what the recipient picker and the gift form already render, so the same
 *     person now reads identically on both sides of the flow (§28/§36).
 *  2. The DATE was passed as `secondaryText`, i.e. as if it were part of the person's
 *     identity — which is also why it had to displace the handle. It is a property of the
 *     GIFT, not of the sender, so it moves out of the person row and right-aligns against
 *     it: the same left-label / right-value axis every row of `OwnerProvenanceCard` uses.
 */
export const GiftInInfoSection = ({ certificate }: { certificate: ICertificateFullInfo }) => {
  const { fromCustomer } = certificate.transfer ?? {};

  /**
   * VRG-486 — an anonymous gift has no sender to render, because the API no longer
   * sends one.
   *
   * Measured before the fix, on a live anonymous gift: this row read "@spencer2",
   * and the certificates list beside it read the sender's EMAIL — the promise the
   * toggle makes ("Hide your name from the recipient") was kept in the gift email
   * and nowhere else.
   *
   * The card is not what enforces it now; the serializer is. What the card owes is a
   * sensible thing to draw in the gap. Passing `name` explicitly rather than letting
   * `UserPreview` resolve a null user matters: the resolver's own last-resort output
   * for "nobody" is an em-dash placeholder, which mid-sentence under "Gift received
   * from" reads as a rendering bug rather than as a deliberate withholding.
   *
   * `avatarUrl` stays undefined so the row falls back to the initial glyph — "A" for
   * Anonymous. A face would be a claim about a person we are declining to identify.
   */
  const anonymous = isAnonymousGift(certificate.transfer as never);

  return (
    <Stack direction='row' alignItems='center' spacing={1.5}>
      {/* `UserPreview` renders avatar + name + sub-line as siblings, so the row wrapper
          (and its gap) belongs to the caller — its own contract. */}
      {anonymous ? (
        <UserPreview name={ANONYMOUS_SENDER_LABEL} />
      ) : (
        <UserPreview user={fromCustomer} avatarUrl={fromCustomer?.avatar} />
      )}
      {/* Top-aligned to the NAME line rather than centred on the whole two-line block,
          so a sender with no handle (one line) and one with a handle (two) put the date
          in the same place. */}
      <Box sx={{ flexShrink: 0, alignSelf: 'flex-start' }}>
        <Typography sx={{ fontSize: 14, color: 'text.secondary', whiteSpace: 'nowrap' }}>
          {formatedDate({
            date: certificate.transfer?.created_at,
            /*
             * DATE ONLY. `MMM do yyyy, h:mm a` rendered "Aug 30th 2026, 9:22 PM" —
             * 22 characters sharing one row with the sender's name, on a 375px
             * phone. It wrapped, and the wrap pushed everything below it down.
             *
             * An earlier note here recorded that a shorter format was tried and
             * rejected "because `availableFormats` is a closed union and adding to
             * it is a decision about the whole app". That is no longer the trade:
             * `MMM do yyyy` is ALREADY in the union — added for date-only fields,
             * explicitly as "the same canonical shape WITHOUT a time" — so this
             * takes an existing app-wide format rather than inventing one.
             *
             * The TIME is not lost, which is what makes this safe. Nothing on this
             * card reads it, and the same handover carries its full timestamp in
             * Provenance → "Owned since" (`OwnerProvenanceCard`, still
             * `MMM do yyyy, h:mm a`) the moment the gift is claimed. A pending
             * gift's minute is not information anyone acts on; the day is.
             *
             * `GiftOutInfoSection` now takes this format too, because it now has this
             * LAYOUT too — it was rebuilt on the same shared row, so the date and the
             * name share a width budget there as well and the exemption this note used
             * to carry no longer describes it. The two halves of the gift flow date it
             * identically.
             *
             * `TransferInInfoSection` is still exempt and still stacked: its date sits
             * on its own line beneath the name, where it competes with nothing and has
             * no wrap pressure.
             */
            format: 'MMM do yyyy',
          })}
        </Typography>
      </Box>
    </Stack>
  );
};
